import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserLabel } from '@/components/ui/user-label'
import { US_STATES } from '@/lib/usStates'
import {
  type IngestFromCabParams,
  type IngestionStatusResult,
  useIngestFromCab,
  useInstantlyCampaigns,
} from '@/models/salesLead'
import { useTaskStatus } from '@/models/tasks'
import { type StaffUser, staffUserName } from '@/models/user'

const DEFAULT_STATES = ['GA', 'IL']
const AUTO_ASSIGN_VALUE = '__auto__'
const DONT_ASSIGN_VALUE = '__dont_assign__'
const NO_CAMPAIGN_VALUE = '__none__'

interface IngestFromCabDialogProps {
  staffUsers: StaffUser[]
}

export function IngestFromCabDialog({ staffUsers }: IngestFromCabDialogProps) {
  const [open, setOpen] = useState(false)
  const [selectedStates, setSelectedStates] = useState<string[]>(DEFAULT_STATES)
  const [truckMin, setTruckMin] = useState('6')
  const [truckMax, setTruckMax] = useState('20')
  const [insExpMin, setInsExpMin] = useState('0')
  const [insExpMax, setInsExpMax] = useState('28')
  const [assignedRepId, setAssignedRepId] = useState(AUTO_ASSIGN_VALUE)
  const [campaignSearch, setCampaignSearch] = useState('')
  const [campaignId, setCampaignId] = useState(NO_CAMPAIGN_VALUE)

  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: campaigns = [], isLoading: campaignsLoading } = useInstantlyCampaigns(
    campaignSearch || undefined,
    open
  )
  const ingestMutation = useIngestFromCab()
  const { data: ingestionStatus } = useTaskStatus<IngestionStatusResult>(taskId)

  useEffect(() => {
    if (!taskId || !ingestionStatus) return
    const { status, result } = ingestionStatus
    if (status === 'COMPLETED') {
      const summary = result
        ? `${result.created} created, ${result.updated} updated, ${result.skipped} skipped`
        : 'done'
      toast.success(`CAB ingestion complete — ${summary}`, { id: taskId, duration: 10000 })
      setTaskId(null)
    } else if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'TERMINATED') {
      toast.error('CAB ingestion failed', { id: taskId })
      setTaskId(null)
    } else if (status !== 'RUNNING' && status !== 'UNKNOWN') {
      // CONTINUED_AS_NEW or any other unexpected terminal status — dismiss gracefully
      toast.dismiss(taskId)
      setTaskId(null)
    }
  }, [taskId, ingestionStatus])

  function toggleState(state: string) {
    setSelectedStates((prev) => (prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state]))
  }

  function handleSubmit() {
    if (selectedStates.length === 0) {
      toast.error('Select at least one state')
      return
    }
    if (Number(truckMin) < 1) {
      toast.error('Truck count min must be at least 1')
      return
    }
    if (Number(truckMax) > 500) {
      toast.error('Truck count max cannot exceed 500')
      return
    }
    if (Number(truckMin) > Number(truckMax)) {
      toast.error('Truck count min must be less than or equal to max')
      return
    }
    if (Number(insExpMin) < 0) {
      toast.error('Insurance expiration min cannot be negative')
      return
    }
    if (Number(insExpMax) > 365) {
      toast.error('Insurance expiration max cannot exceed 365 days')
      return
    }
    if (Number(insExpMin) > Number(insExpMax)) {
      toast.error('Insurance expiration min must be less than or equal to max')
      return
    }

    const params: IngestFromCabParams = {
      states: selectedStates,
      truckMin: Number(truckMin) || 1,
      truckMax: Number(truckMax) || 10,
      insExpMin: Number(insExpMin) || 7,
      insExpMax: Number(insExpMax) || 60,
      assignedRepId:
        assignedRepId === AUTO_ASSIGN_VALUE || assignedRepId === DONT_ASSIGN_VALUE
          ? undefined
          : assignedRepId,
      campaignId: campaignId === NO_CAMPAIGN_VALUE ? undefined : campaignId,
      skipAssignment: assignedRepId === DONT_ASSIGN_VALUE,
    }

    ingestMutation.mutate(params, {
      onSuccess: (data) => {
        const tid = data.taskId
        const campaignNote = campaignId ? ' Leads with emails will be added to the selected campaign.' : ''
        toast.loading(`CAB ingestion running…${campaignNote}`, { id: tid, duration: Infinity })
        setTaskId(tid)
        handleOpenChange(false)
      },
      onError: () => {
        toast.error('Failed to start CAB ingestion')
      },
    })
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      setSelectedStates(DEFAULT_STATES)
      setTruckMin('6')
      setTruckMax('20')
      setInsExpMin('0')
      setInsExpMax('28')
      setAssignedRepId(AUTO_ASSIGN_VALUE)
      setCampaignSearch('')
      setCampaignId(NO_CAMPAIGN_VALUE)
    }
    setOpen(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">Ingest from CAB</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ingest from CAB</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {/* States */}
          <div className="flex flex-col gap-1.5">
            <Label>States</Label>
            <div className="flex flex-wrap gap-1.5">
              {US_STATES.map(([code]) => (
                <Button
                  key={code}
                  type="button"
                  variant={selectedStates.includes(code) ? 'default' : 'outline'}
                  onClick={() => toggleState(code)}
                  className="h-auto rounded px-2 py-0.5 text-xs font-medium"
                >
                  {code}
                </Button>
              ))}
            </div>
            {selectedStates.length === 0 && (
              <p className="text-destructive text-xs">Select at least one state</p>
            )}
          </div>

          {/* Truck range */}
          <div className="flex flex-col gap-1.5">
            <Label>Truck count</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                value={truckMin}
                onChange={(e) => setTruckMin(e.target.value)}
                className="w-20"
                placeholder="Min"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="number"
                min={1}
                value={truckMax}
                onChange={(e) => setTruckMax(e.target.value)}
                className="w-20"
                placeholder="Max"
              />
            </div>
          </div>

          {/* Expiration window */}
          <div className="flex flex-col gap-1.5">
            <Label>Insurance expiring in (days from today)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                value={insExpMin}
                onChange={(e) => setInsExpMin(e.target.value)}
                className="w-20"
                placeholder="Min"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="number"
                min={0}
                value={insExpMax}
                onChange={(e) => setInsExpMax(e.target.value)}
                className="w-20"
                placeholder="Max"
              />
            </div>
          </div>

          {/* Assignee */}
          <div className="flex flex-col gap-1.5">
            <Label>Assign to</Label>
            <Select value={assignedRepId} onValueChange={setAssignedRepId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_ASSIGN_VALUE}>Auto-assign (Debbie / Lupita)</SelectItem>
                <SelectItem value={DONT_ASSIGN_VALUE}>Don&apos;t assign (leave unassigned)</SelectItem>
                {staffUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    <UserLabel name={staffUserName(u)} src={u.avatar} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Campaign (optional) */}
          <div className="flex flex-col gap-1.5">
            <Label>
              Add to Instantly Campaign <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              placeholder="Search by name…"
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              className="mb-1"
            />
            <Select value={campaignId} onValueChange={setCampaignId} disabled={campaignsLoading}>
              <SelectTrigger>
                <SelectValue placeholder={campaignsLoading ? 'Loading…' : 'Select a campaign'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CAMPAIGN_VALUE}>None</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
                {!campaignsLoading && campaigns.length === 0 && (
                  <div className="text-muted-foreground px-3 py-2 text-sm">No campaigns found</div>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedStates.length === 0 || ingestMutation.isPending}
            loading={ingestMutation.isPending ? 'Starting…' : undefined}
          >
            Ingest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
